import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  Modal,
  NumberInput,
  RemoveScroll,
  ScrollArea,
  Select,
  Table,
  Tooltip
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IOrder, IOrderPaymentLines } from '../../interfaces/Order';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import moment from 'moment';
import { formatMoneyString } from '../../utils/strings';
import { useHistory } from 'react-router-dom';
import { ExtendedPaymentConfigEnum } from '../../enum/PaymentConfig';
import { IconCircleX, IconDeviceFloppy, IconEdit, IconTrash } from '@tabler/icons-react';
import { memo, useState } from 'react';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { showNotification } from '../../utils/Notification/Notification';
import { useDisclosure } from '@mantine/hooks';
import OrderAddPaymentLineModal from './OrderAddPaymentLineModal';
import { useDeletePaymentLineById, useUpdatePayementLineById } from '../../hooks/orders/mutations';
import { useSelector } from 'react-redux';
import useCanShowFiscalFeature, {
  FiscalFeature
} from '../../hooks/fiscal/useCanShowFiscalFeature';
import { PaymentMethodsEnum } from '../../enum/PaymentMethodsEnum';

type PermissionGate = {
  canMutate?: boolean;
};

type PaymentMutationUser = {
  permission?: Record<string, PermissionGate>;
};

type UserStateSlice = {
  user: PaymentMutationUser;
};

type RootUserState = {
  user: UserStateSlice;
};

type EditablePaymentMethod =
  | PaymentMethodsEnum.Cash
  | PaymentMethodsEnum.Card
  | PaymentMethodsEnum.Transfer;

const editablePaymentMethods: EditablePaymentMethod[] = [
  PaymentMethodsEnum.Cash,
  PaymentMethodsEnum.Card,
  PaymentMethodsEnum.Transfer
];

const getEditablePaymentMethod = (paymentMethod?: string): EditablePaymentMethod | null => {
  if (
    paymentMethod === PaymentMethodsEnum.Cash ||
    paymentMethod === PaymentMethodsEnum.Card ||
    paymentMethod === PaymentMethodsEnum.Transfer
  ) {
    return paymentMethod;
  }

  return null;
};

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

const hasMpMetadata = (mp: IOrderPaymentLines['mp']) => {
  if (!mp) return false;

  return Object.values(mp).some(hasValue);
};

const hasExternalPaymentRefs = (paymentLine: IOrderPaymentLines) =>
  hasValue(paymentLine.attemptId) ||
  hasValue(paymentLine.providerRefId) ||
  hasMpMetadata(paymentLine.mp);

const isProviderBackedPayment = (paymentLine: IOrderPaymentLines) =>
  !!paymentLine.provider && paymentLine.provider !== 'internal';

const isAmountEditLocked = (paymentLine: IOrderPaymentLines) =>
  paymentLine.isOnACashUpReport ||
  !!paymentLine.facturapiPaymentInvoiceID ||
  !!paymentLine.facturapiInvoiceID;

const canEditPaymentMethod = (paymentLine: IOrderPaymentLines) =>
  !!getEditablePaymentMethod(paymentLine.paymentMethod) &&
  !isAmountEditLocked(paymentLine) &&
  !isProviderBackedPayment(paymentLine) &&
  !hasExternalPaymentRefs(paymentLine);

export default memo(function OrderPaymentLinesModal({
  opened,
  close,
  title,
  order,
  onDeletePaymentLine = () => {},
  onUpdatePaymentLine = () => {},
  onAddPaymentLine = () => {},
  onPaymentPendingChange,
  pinUser
}: {
  opened: boolean;
  close: () => void;
  title: string;
  order: IOrder | null;
  onDeletePaymentLine: () => void;
  onUpdatePaymentLine: () => void;
  onAddPaymentLine: () => void;
  onPaymentPendingChange?: (pending: boolean) => void;
  pinUser?: {
    _id: string;
    name: string;
    cashierBox: string;
    permission: any;
  } | null;
}) {
  const { t } = useTranslation(['FieldName', 'OrdersPage']);
  const history = useHistory();
  const dataSource = order?.paymentLines || [];
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [amountPaidEdit, setAmountPaidEdit] = useState(0);
  const [paymentMethodEdit, setPaymentMethodEdit] = useState<EditablePaymentMethod>(
    PaymentMethodsEnum.Card
  );
  const [openedPaymentLines, handlers] = useDisclosure(false);
  const deletePaymentLineById = useDeletePaymentLineById();
  const updatePayementLineById = useUpdatePayementLineById();
  const { user } = useSelector((state: RootUserState) => state.user);
  const mutateOrderPayments = (pinUser || user)?.permission['mutateOrderPayments']?.canMutate; //IF PIN USER IS NULL, USE USER PERMISSIONS TO VALIDATE
  const canShowMexicanCFDIFields = useCanShowFiscalFeature(FiscalFeature.MEXICO_CFDI_FIELDS);
  const editablePaymentMethodOptions = editablePaymentMethods.map((paymentMethod) => ({
    value: paymentMethod,
    label: t(`FieldName:paymentMethods.${paymentMethod}`)
  }));

  const startEditing = (paymentLine: IOrderPaymentLines) => {
    setEditingRow(paymentLine._id);
    setAmountPaidEdit(Number(paymentLine.amountPaid || 0));
    setPaymentMethodEdit(getEditablePaymentMethod(paymentLine.paymentMethod) || PaymentMethodsEnum.Cash);
  };

  const rows = dataSource.map((element: IOrderPaymentLines) => {
    const cfdiId = element.facturapiPaymentInvoiceID || element.facturapiInvoiceID;
    const invoiceType =
      element.facturapiInvoiceType ||
      (element.facturapiPaymentInvoiceID ? 'payment_invoice' : null);

    return (
      <Table.Tr key={element._id}>
        <Table.Td>
          {editingRow !== element._id && (
            <Flex justify="flex-start" align="flex-start" direction="row" wrap="nowrap">
              <Tooltip label={t('CommonButtons:edit')}>
                <ActionIcon
                  data-testid={`payment-line-edit-${element._id}`}
                  disabled={!!editingRow || !mutateOrderPayments}
                  radius={0}
                  variant="subtle"
                  w={'100%'}
                  onClick={() => startEditing(element)}
                >
                  <IconEdit />
                </ActionIcon>
              </Tooltip>

              <Tooltip label={t('CommonButtons:delete')}>
                <ActionIcon
                  disabled={element.isOnACashUpReport || !!editingRow || !mutateOrderPayments}
                  radius={0}
                  variant="subtle"
                  w={'100%'}
                  onClick={() =>
                    ConfirmationModal({
                      title: t('Notifications:CRUD.deleteConfirmation'),
                      onConfirm: () => onDelete(order?._id || '', element._id),
                      onCancel: () => {}
                    })
                  }
                >
                  <IconTrash />
                </ActionIcon>
              </Tooltip>
            </Flex>
          )}
          {editingRow === element._id && (
            <Flex>
              <Tooltip label={t('CommonButtons:submit')}>
                <ActionIcon
                  data-testid={`payment-line-save-${element._id}`}
                  disabled={isAmountEditLocked(element)}
                  radius={0}
                  variant="subtle"
                  w={'100%'}
                  onClick={async () => {
                    await onUpdate(
                      order?._id || '',
                      element._id,
                      amountPaidEdit,
                      canEditPaymentMethod(element) ? paymentMethodEdit : undefined
                    );
                  }}
                >
                  <IconDeviceFloppy />
                </ActionIcon>
              </Tooltip>

              <Tooltip label={t('CommonButtons:cancel')}>
                <ActionIcon
                  radius={0}
                  variant="subtle"
                  w={'100%'}
                  onClick={() => setEditingRow(null)}
                >
                  <IconCircleX />
                </ActionIcon>
              </Tooltip>
            </Flex>
          )}
        </Table.Td>
        <Table.Td>{moment(element.paymentDate).format('DD/MM/YYYY HH:mm')}</Table.Td>
        <Table.Td>
          {editingRow === element._id && getEditablePaymentMethod(element.paymentMethod) ? (
            <Select
              allowDeselect={false}
              data={editablePaymentMethodOptions}
              data-testid={`payment-line-method-select-${element._id}`}
              disabled={!canEditPaymentMethod(element)}
              value={paymentMethodEdit}
              w={180}
              onChange={(value) => {
                const paymentMethod = getEditablePaymentMethod(value || undefined);

                if (paymentMethod) {
                  setPaymentMethodEdit(paymentMethod);
                }
              }}
            />
          ) : (
            t(
              `FieldName:paymentMethods.${
                ExtendedPaymentConfigEnum[
                  element.paymentMethod.toUpperCase() as keyof typeof ExtendedPaymentConfigEnum
                ]
              }`
            )
          )}
        </Table.Td>
        <Table.Td>
          {editingRow === element._id ? (
            <NumberInput
              allowDecimal
              allowLeadingZeros={false}
              data-testid={`payment-line-amount-${element._id}`}
              disabled={isAmountEditLocked(element)}
              value={amountPaidEdit}
              onChange={(val) => {
                setAmountPaidEdit(+val);
              }}
            />
          ) : (
            formatMoneyString(element.amountPaid.toFixed(2))
          )}
        </Table.Td>
        <Table.Td>{element.isOnACashUpReport ? t('FieldName:yes') : t('FieldName:no')}</Table.Td>
        {canShowMexicanCFDIFields && (
          <Table.Td>
            {invoiceType ? t(`OrdersPage:paymentLinesModal.invoiceTypes.${invoiceType}`) : '-'}
          </Table.Td>
        )}
        {canShowMexicanCFDIFields && (
          <Table.Td>
            {cfdiId ? (
              <a
                href={`/CFDI?q=${cfdiId}`}
                onClick={(event) => {
                  event.preventDefault();
                  history.push(`/CFDI?q=${cfdiId}`);
                }}
              >
                {cfdiId}
              </a>
            ) : (
              '-'
            )}
          </Table.Td>
        )}
      </Table.Tr>
    );
  });

  const onUpdate = async (
    orderId: string,
    id: string,
    amountPaid: number,
    paymentMethod?: EditablePaymentMethod
  ) => {
    onPaymentPendingChange?.(true);
    try {
      await updatePayementLineById.mutateAsync({
        orderId: orderId,
        id: id,
        amountPaid: amountPaid,
        paymentMethod,
        updatedDate: new Date(),
        pinUserId: pinUser?._id
      });
      await onUpdatePaymentLine();
      showNotification({ type: 'Default', title: t('Notifications:success') });
      setEditingRow(null);
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    } finally {
      onPaymentPendingChange?.(false);
    }
  };

  const onDelete = async (orderId: string, id: string) => {
    onPaymentPendingChange?.(true);
    try {
      await deletePaymentLineById.mutateAsync({
        orderId,
        id
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      await onDeletePaymentLine();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    } finally {
      onPaymentPendingChange?.(false);
    }
  };

  return (
    <Modal
      data-testid="order-payment-lines-modal"
      opened={opened}
      onClose={() => {
        setEditingRow(null);
        close();
      }}
      closeButtonProps={{ 'data-testid': 'order-payment-lines-close' }}
      className={RemoveScroll.classNames.fullWidth}
      title={title}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <LoaderOverlay
        visible={!order || deletePaymentLineById.isPending || updatePayementLineById.isPending}
      />
      <>
        <OrderAddPaymentLineModal
          opened={openedPaymentLines}
          order={order}
          close={handlers.close}
          title={t('OrdersPage:paymentLinesModal.add')}
          onAddPaymentLine={onAddPaymentLine}
          onPaymentPendingChange={onPaymentPendingChange}
          pinUser={pinUser || null}
        />
        <Table striped highlightOnHover withTableBorder withColumnBorders width={'100%'}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('OrdersPage:paymentLinesModal.actions')}</Table.Th>
              <Table.Th>{t('OrdersPage:paymentLinesModal.date')}</Table.Th>
              <Table.Th>{t('OrdersPage:paymentLinesModal.paymentMethod')}</Table.Th>
              <Table.Th>{t('OrdersPage:paymentLinesModal.amount')}</Table.Th>
              <Table.Th>{t('OrdersPage:paymentLinesModal.isOnACashUpReport')}</Table.Th>
              {canShowMexicanCFDIFields && (
                <Table.Th>{t('OrdersPage:paymentLinesModal.invoiceType')}</Table.Th>
              )}
              {canShowMexicanCFDIFields && (
                <Table.Th>{t('OrdersPage:paymentLinesModal.cfdi')}</Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
        <Divider my={5} />
        <Button
          data-testid="order-payment-lines-open-add"
          type="submit"
          fullWidth
          disabled={!!editingRow}
          onClick={() => {
            handlers.open();
          }}
        >
          {t('CommonButtons:add')}
        </Button>
      </>
    </Modal>
  );
});
